import { useArrowSource } from '@components/ArtBoard/Arrows/hooks/useArrowSource'
import type { ArrowUI } from '@components/ArtBoard/Arrows/types'
import type { ArrowLabel } from '@octopus-do/octopus-project-core'
import { thunkUpdateArrowLabel } from '@store/migrationActions/thunkActions/thunkArrowsActions'
import {
  artBoardActions,
  selectSelectedArrowLabel,
} from '@store/reducers/artboard'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import cx from 'classnames'
import type React from 'react'
import { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import type { AppState } from '@/types'
import { ARROW_LABEL_DRAG_THRESHOLD } from '../../constants'
import {
  getArrowLine,
  getClosestShare,
  type Point,
  placeLabel,
} from '../../utils'
import css from './styles.module.css'

interface Props {
  arrow: ArrowUI
  canEdit: boolean
}

export const ArrowLabelText: React.FC<Props> = ({ arrow, canEdit }) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()
  const { color } = useArrowSource(arrow.relation[0], arrow.colorId)
  const selected = useSelector(selectSelectedArrowLabel) === arrow.id

  // The stored label is always there: the layer renders this only for a label.
  const label = arrow.label as ArrowLabel

  const pressRef = useRef<Point | null>(null)
  const draggedRef = useRef(false)

  const moveToPointer = (element: HTMLElement, x: number, y: number) => {
    const share = getClosestShare(getArrowLine(arrow.id), { x, y })

    if (share !== null) {
      element.dataset.position = String(share)
      placeLabel(element)
    }
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!canEdit || e.button !== 0) {
      return
    }

    e.currentTarget.setPointerCapture(e.pointerId)
    pressRef.current = { x: e.clientX, y: e.clientY }
    draggedRef.current = false
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const press = pressRef.current

    if (!press) {
      return
    }

    const travel = Math.hypot(e.clientX - press.x, e.clientY - press.y)

    if (draggedRef.current || travel >= ARROW_LABEL_DRAG_THRESHOLD) {
      draggedRef.current = true
      moveToPointer(e.currentTarget, e.clientX, e.clientY)
    }
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    pressRef.current = null

    if (draggedRef.current) {
      dispatch(
        thunkUpdateArrowLabel({
          id: arrow.id,
          label: {
            ...label,
            position: Number(e.currentTarget.dataset.position),
          },
        })
      )
    }
  }

  // A gesture the system took over: the label goes back to where it is stored.
  const onPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    pressRef.current = null
    draggedRef.current = false
    e.currentTarget.dataset.position = String(label.position)
    placeLabel(e.currentTarget)
  }

  // As on a sticky note: the first click selects, the second one starts editing.
  const onClick = () => {
    if (draggedRef.current) {
      return
    }

    if (selected) {
      dispatch({ type: CanvasActionTypes.RESET_SELECTED_ARROW_ID })
      dispatch(artBoardActions.setEditingArrowLabel(arrow.id))

      return
    }

    dispatch({
      type: CanvasActionTypes.SET_SELECTED_ARROW_ID,
      payload: { id: arrow.id },
    })
    dispatch({ type: CanvasActionTypes.RESET_SELECTED_ARROW_POINT_ID })
    dispatch(artBoardActions.setSelectedArrowLabel(arrow.id))
  }

  return (
    <div
      className={cx(
        css.label,
        `Arrow-model-${arrow.model}`,
        arrow.type === 'smooth' && css.smooth,
        arrow.opacityState === 'dimmed' && css.dimmed,
        canEdit && css.editable,
        canEdit && selected && css.selected
      )}
      data-arrow-id={arrow.id}
      data-position={label.position}
      style={{ '--arrow-color': color } as React.CSSProperties}
      {...(canEdit
        ? {
            onClick,
            onPointerCancel,
            onPointerDown,
            onPointerMove,
            onPointerUp,
          }
        : {})}
    >
      {label.text}
    </div>
  )
}
